/* eslint-disable no-param-reassign */

import {Trans} from '@lingui/macro';
import produce, {castDraft} from 'immer';
import {flatMap} from 'lodash';
import React, {FC, ReactNode} from 'react';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {decorateWithClientId} from '../../../editors/commons/clientIdModel';
import {Expression} from '../../../editors/expressions/expressionModel';
import {useExpressionsContext} from '../../../editors/expressions/expressionsContext';
import {TeammateListResult, TeammateResult} from '../../../editors/expressions/infer/expressionResultModel';
import {
  ANDConditions,
  AssigneeChanged,
  AssigneeIn,
  isAssigneeChangedOrAssigneeIn,
  RuleConditionTypesEnum,
} from '../../ruleConditionModel';
import {TriggerWithConditions} from '../../ruleTriggerHelpers';
import {useOnSelectCreateExpressionRef} from '../hooks/useOnSelectCreateExpression';
import {TeammateSelectedDropdownItem, TeammatesParameter} from '../parameters/teammateParameter';
import {
  MultilineRuleParameterCardDefaultTitleStyled,
  RuleMultilineParameterCard,
} from '../ruleMultilineParameterCard';
import {RuleTriggerIcon} from '../ruleTriggerIcon';

/*
 * Types.
 */

export interface RuleAssigneeIsChangedTriggerCardProps {
  triggerWithConditions: TriggerWithConditions;
  isDisabled?: boolean;
  errorMessage?: ReactNode;
  expressions: ReadonlyArray<Expression>;

  onUpdateConditions: HandlerOf<ANDConditions>;
  onDelete?: Handler;
}

type ConditionAssigneeInOrChanged =
  | RuleConditionTypesEnum.ASSIGNEE_IN
  | RuleConditionTypesEnum.ASSIGNEE_CHANGED;

/*
 * Component.
 */

export const RuleAssigneeIsChangedTriggerCard: FC<RuleAssigneeIsChangedTriggerCardProps> = (props) => {
  const {triggerWithConditions, isDisabled, errorMessage, onUpdateConditions, onDelete} = props;
  const {trigger, conditions} = triggerWithConditions;
  const {expressions} = useExpressionsContext();

  const currentValue = flatMap(conditions, (orConditions) =>
    flatMap(orConditions.filter(isAssigneeChangedOrAssigneeIn), (condition) => condition.params.teammates),
  );

  const onChange = (updatedTeammateIds: OrNull<ReadonlyArray<TeammateSelectedDropdownItem>>) => {
    const updatedConditions = produce(conditions, (draft) => {
      // Clear the first 'AND' node containing "mentioned teammate condition".
      // We are only targeting the first available condition.

      let assigneeConditionType: ConditionAssigneeInOrChanged = RuleConditionTypesEnum.ASSIGNEE_CHANGED;

      draft.find((orConditions, index) => {
        const assigneeChangedOrInIndex = orConditions.findIndex(isAssigneeChangedOrAssigneeIn);
        const hasAssigneeChangedOrInCondition = assigneeChangedOrInIndex >= 0;

        if (hasAssigneeChangedOrInCondition) {
          assigneeConditionType = orConditions[assigneeChangedOrInIndex].type as ConditionAssigneeInOrChanged;

          orConditions.splice(assigneeChangedOrInIndex, 1);

          // Remove empty condition nodes.
          if (orConditions.length === 0) {
            draft.splice(index, 1);
          }
        }

        // Return early if we found a condition.
        return hasAssigneeChangedOrInCondition;
      });

      const updatedCondition = castDraft(
        decorateWithClientId({
          integration_id: 0,
          type: assigneeConditionType,
          params: {
            teammates: updatedTeammateIds,
          },
        } as AssigneeChanged | AssigneeIn),
      );

      // Add an 'AND' node with the selected teammates.
      draft.push([updatedCondition]);
    });

    onUpdateConditions(updatedConditions);
  };

  const onSelectCreateExpression = useOnSelectCreateExpressionRef(
    (expressionRef) => onChange([...currentValue, expressionRef]),
    [TeammateResult, TeammateListResult],
  );

  const title = (
    <>
      <MultilineRuleParameterCardDefaultTitleStyled>
        <Trans id="rule-editor-trigger-assign-title">Assignee is changed to</Trans>
      </MultilineRuleParameterCardDefaultTitleStyled>
      <TeammatesParameter
        selectedItems={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={onChange}
        canSelectAnyItem
        onSelectCreateExpression={onSelectCreateExpression}
      />
    </>
  );

  return (
    <RuleMultilineParameterCard
      title={title}
      icon={<RuleTriggerIcon trigger={trigger} />}
      errorMessage={errorMessage}
      onCancel={onDelete}
    />
  );
};
